import type { DatasetRowChangeType } from '../dataset/dataset-trigger-criterion';
import { isInternalDatasetColumn } from '../dataset/dataset-row-key';
import type { JsonValue } from '../serializable';
import { toJsonObject } from './build-webhook-run-input';

export type DatasetRunInputParams = {
  readonly datasetId: string;
  readonly datasetName: string;
  readonly rowKey: string;
  readonly changeType: DatasetRowChangeType;
  /** The row as it stands now. Null for a deletion — the row is gone. */
  readonly row: unknown;
  /** Tracked values from the previous evaluation; null the first time. */
  readonly before: unknown;
  readonly changedFields: readonly string[];
};

/**
 * Where the trigger describes the change, alongside the row's own columns.
 *
 * Underscored so it cannot plausibly collide with a column someone named: the
 * columns are the user's to name and this one key is ours, so it takes the
 * shape no dataset column has.
 */
export const DATASET_RUN_INPUT_META_KEY = '__meta';

const changedFieldsBetween = (
  before: unknown,
  after: unknown
): readonly string[] => {
  const previous = toJsonObject(before);
  const current = toJsonObject(after);
  const names = new Set([...Object.keys(previous), ...Object.keys(current)]);
  return [...names]
    .filter(
      (name) => JSON.stringify(previous[name]) !== JSON.stringify(current[name])
    )
    .sort();
};

export const datasetChangedFields = changedFieldsBetween;

/** The row's user-facing columns — bookkeeping columns are never reported. */
const reportableRowFields = (row: unknown): { [key: string]: JsonValue } => {
  const fields = toJsonObject(row);
  const out: { [key: string]: JsonValue } = {};
  for (const [name, value] of Object.entries(fields)) {
    if (isInternalDatasetColumn(name)) {
      continue;
    }
    out[name] = value;
  }
  return out;
};

/**
 * Merges trigger binding defaults with the row that caused the run.
 *
 * The row's columns sit at the top level, so a downstream node binds straight
 * to a column name — `__trigger__/email` for the column called `email`.
 * Everything the trigger has to say about the change goes under
 * the single {@link DATASET_RUN_INPUT_META_KEY} key instead of alongside the
 * columns: put `rowKey`, `changeType` and friends at the top level too — which
 * this used to do — and a dataset with a column called `rowKey` silently loses
 * it to the metadata.
 *
 * `after` is gone: it *is* the top level. `before` keeps its place under the
 * metadata, since nothing else can reconstruct it — it comes from the trigger's
 * observed-state, which has already moved on by the time a dispatch runs.
 *
 * Binding defaults go in first, so a column wins over a default of the same
 * name: the run is about that row, and the default is what stands in when there
 * is nothing to say.
 *
 * @see isDatasetRunInput, which is how a consumer recognises this payload in
 * order to leave it alone rather than merging it.
 */
export function buildDatasetRunInput(
  defaultInput: unknown,
  params: DatasetRunInputParams
): { [key: string]: JsonValue } {
  const base = toJsonObject(defaultInput);
  return {
    ...base,
    // Nothing at all for a deletion: the row is gone, so it has no columns.
    ...reportableRowFields(params.row),
    [DATASET_RUN_INPUT_META_KEY]: {
      datasetId: params.datasetId,
      datasetName: params.datasetName,
      rowKey: params.rowKey,
      changeType: params.changeType,
      changedFields: [...params.changedFields],
      before: params.before === null ? null : toJsonObject(params.before),
    },
  };
}

/**
 * A representative run input for a dataset trigger, for the editor's field
 * pickers and the binding validator.
 *
 * Built by {@link buildDatasetRunInput} rather than hand-copied, so the pointers
 * offered are exactly the ones a run carries. Both the builder's `/` picker and
 * the server's validator ask for it here for the same reason: they disagreed
 * once already, and a picker that offers a pointer the validator then rejects is
 * worse than either being wrong alone.
 *
 * `before` is filled from the same columns instead of left null. It is where the
 * changed row's *previous* values live — the natural other half of "the stage
 * moved from X to Y" — and a null stood in for the whole object, so the picker
 * offered `__meta/before` and nothing under it. Nothing else in a dataset
 * payload nests: a column holds a scalar, so the columns themselves are leaves.
 *
 * Every column appears under `before`, not merely the trigger's tracked ones.
 * The over-approximation is the deliberate direction: a run only carries the
 * tracked columns there, but the alternative is a validator that rejects a ref
 * to a column someone is about to start tracking, and a ref that reads nothing
 * is already reported where it is written.
 */
export function sampleDatasetRunInput(params: {
  readonly defaultInput: unknown;
  readonly datasetId: string;
  readonly columns: readonly string[];
}): { [key: string]: JsonValue } {
  const row = Object.fromEntries(params.columns.map((column) => [column, '']));
  return buildDatasetRunInput(params.defaultInput, {
    datasetId: params.datasetId,
    datasetName: '',
    rowKey: '',
    changeType: 'row_added',
    row,
    before: row,
    changedFields: [],
  });
}

const isRecord = (value: unknown): value is { [key: string]: unknown } =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/**
 * Whether this is a dataset trigger's run input.
 *
 * Asked by consumers that would otherwise merge an upstream payload wholesale
 * into something of their own: this one is not merged at all, it is read by
 * explicit ref. See `buildToolCallInput`, where merging it handed every node a
 * dataset trigger fires a copy of the changed row's columns as params it never
 * asked for — alongside the values the node was actually configured with, which
 * name the same columns through refs.
 *
 * Recognition is on `__meta.datasetId` rather than on the key alone, so a
 * webhook that happens to post its own `__meta` is not mistaken for one of ours.
 */
export function isDatasetRunInput(runInput: unknown): boolean {
  if (!isRecord(runInput)) {
    return false;
  }
  const meta = runInput[DATASET_RUN_INPUT_META_KEY];
  return isRecord(meta) && typeof meta['datasetId'] === 'string';
}
